// src/pages/jewellers/CustomerKittyForm.js
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api";
import Loader from "../../components/Loader";
import {
  UserIcon,
  GiftIcon,
  CalendarDaysIcon,
  CurrencyRupeeIcon,
  CircleStackIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";

export default function CustomerKittyForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    customer_id: "",
    kitty_id: "",
    status: "active",
    start_date: "",
    end_date: "",
    target_amount: "",
    notes: "",
  });

  const [customers, setCustomers] = useState([]);
  const [kitties, setKitties] = useState([]);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(""); // 👈 backend error message

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customersRes, kittiesRes] = await Promise.all([
          api.get("/jeweller-customers"),
          api.get("/kitties"),
        ]);
        setCustomers(customersRes.data);
        setKitties(kittiesRes.data);

        if (isEdit) {
          const res = await api.get(`/customer-kitties/view/${id}`);
          setForm({
            ...res.data,
            kitty_id: res.data.kitty_id,
            target_amount: res.data.target_amount,
            status: res.data.status || "active",
          });
        }
      } catch (err) {
        console.error("Failed to fetch data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEdit]);

  useEffect(() => {
    if (form.kitty_id) {
      const selectedKitty = kitties.find(
        (k) => String(k.id) === String(form.kitty_id)
      );
      if (selectedKitty) {
        setForm((prev) => ({
          ...prev,
          target_amount: selectedKitty.target_amount,
        }));
      }
    } else {
      setForm((prev) => ({ ...prev, target_amount: "" }));
    }
  }, [form.kitty_id, kitties]);

  // 👇 Automatically scroll to top when apiError changes
  useEffect(() => {
    if (apiError) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [apiError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setApiError("");

    try {
      if (isEdit) {
        await api.put(`/customer-kitties/${id}`, form);
      } else {
        await api.post("/customer-kitties", form);
      }
      navigate("/jewellers/CustomerKitties");
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else if (err.response?.data?.error) {
        setApiError(err.response.data.error); // 👈 custom backend message
      } else {
        console.error("Save failed:", err);
      }
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="p-8 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-2 mb-8">
        <GiftIcon className="h-7 w-7 text-purple-600" />
        <h1 className="text-3xl font-bold text-gray-800">
          {isEdit ? "Edit Customer Kitty" : "Assign New Kitty"}
        </h1>
      </div>

      {/* 🔴 Tailwind Alert for API Error */}
      {apiError && (
        <div className="mb-6 p-4 rounded-lg bg-red-100 border border-red-300 text-red-800 flex items-center justify-between">
          <span>{apiError}</span>
          <button
            type="button"
            onClick={() => setApiError("")}
            className="text-red-600 font-semibold hover:text-red-800"
          >
            ×
          </button>
        </div>
      )}

      {/* Form Card */}
      <form onSubmit={handleSubmit} className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 space-y-6">
        {/* Customer */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <UserIcon className="h-5 w-5 text-purple-500" /> Customer
          </label>
          <select name="customer_id" value={form.customer_id} onChange={handleChange} required className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-300 outline-none transition">
            <option value="">-- Select Customer --</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.full_name}
              </option>
            ))}
          </select>
          {errors.customer_id && (
            <p className="text-red-600 text-sm mt-1">
              {errors.customer_id[0]}
            </p>
          )}
        </div>

        {/* Kitty */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <GiftIcon className="h-5 w-5 text-purple-500" /> Kitty
          </label>
          <select name="kitty_id" value={form.kitty_id} onChange={handleChange} required className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-300 outline-none transition">
            <option value="">-- Select Kitty --</option>
            {kitties
              .filter((k) => k.status === "active")
              .map((k) => (
                <option key={k.id} value={k.id}>
                  {k.kitty_name} ({k.kitty_type})
                </option>
              ))}
          </select>
          {errors.kitty_id && (
            <p className="text-red-600 text-sm mt-1">
              {errors.kitty_id[0]}
            </p>
          )}
        </div>

        {/* Status */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <PencilSquareIcon className="h-5 w-5 text-purple-500" /> Status
          </label>
          <select name="status" value={form.status} onChange={handleChange} required className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-300 outline-none transition">
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
          </select>
          {errors.status && (
            <p className="text-red-600 text-sm mt-1">{errors.status[0]}</p>
          )}
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              <CalendarDaysIcon className="h-5 w-5 text-purple-500" /> Start Date
            </label>
            <input type="date" name="start_date" value={form.start_date} onChange={handleChange} required className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-300 outline-none transition" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              <CalendarDaysIcon className="h-5 w-5 text-purple-500" /> End Date
            </label>
            <input type="date" name="end_date" value={form.end_date} onChange={handleChange} required className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-300 outline-none transition" />
          </div>
        </div>

        {/* Target Amount */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              <CircleStackIcon className="h-5 w-5 text-purple-500" /> Token Number
            </label>
            <input type="text" name="token_number" value={form.token_number} required className="w-full border rounded-lg px-3 py-2" onChange={handleChange} />
            {errors.token_number && (
              <p className="text-red-600 text-sm mt-1">
                {errors.token_number[0]}
              </p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              <CurrencyRupeeIcon className="h-5 w-5 text-purple-500" /> Target Amount
            </label>
            <input type="number" name="target_amount" value={form.target_amount} readOnly required className="w-full border border-gray-300 bg-gray-100 rounded-lg px-3 py-2" />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <PencilSquareIcon className="h-5 w-5 text-purple-500" /> Notes
          </label>
          <textarea name="notes" value={form.notes} onChange={handleChange} rows="4" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-300 outline-none transition" />
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button type="submit" className="bg-purple-600 text-white px-6 py-2 rounded-lg shadow-md hover:bg-purple-700 transition-all duration-200">
            {isEdit ? "Update Kitty" : "Assign Kitty"}
          </button>
        </div>
      </form>
    </div>
  );
}
